import { Brand } from "@/utils/brand";
import {
  CollectionReference,
  DocumentData,
  DocumentReference,
  Query,
  QueryConstraint,
  QuerySnapshot,
  Timestamp,
} from "firebase/firestore";
import { Atom } from "jotai";
import { DocumentResultAtom } from "./atoms/observeFirestoreDocumentAtom";
import { CreateEntityOptions } from "./entity";

export type EntityRecord = {
  version: string;
  creatorId: string;
  status: "published" | "trashed";
  createdAt: Timestamp;
  updatedAt: Timestamp;
};

export type LockedRecordProps = keyof EntityRecord;
export type PartialEntityRecord<T> = Omit<Partial<T>, LockedRecordProps>;

export type BelongsToIds<T> = Brand<
  string[],
  `${Extract<keyof T, string>}BelongsTo`
>;

export function belongsToIds<T>(ids: string[] = []) {
  return ids as BelongsToIds<T>;
}

export type BelongsToRelationPropsOfType<T, K> = keyof {
  [P in keyof T as T[P] extends BelongsToIds<K> ? P : never]: T[P];
};

export type Entity<EntityRecordType extends EntityRecord = EntityRecord> = {
  id: string;
  data: EntityRecordType | null | undefined;
  docRef: DocumentReference<DocumentData, DocumentData>;
  model: Model<EntityRecordType>;
  hydrate: () => Promise<EntityRecordType>;
  update: (newData: PartialEntityRecord<EntityRecordType>) => Promise<void>;
  remove: () => Promise<void>;
  createBelongsToRelation: <K extends EntityRecord>(
    entity: Entity<K>,
    propId: BelongsToRelationPropsOfType<EntityRecordType, K>,
  ) => Promise<void>;
  removeBelongsToRelation: <K extends EntityRecord>(
    entity: Entity<K>,
    propId: BelongsToRelationPropsOfType<EntityRecordType, K>,
  ) => Promise<void>;
  getObserveEntityRecordAtom: () => DocumentResultAtom<EntityRecordType>;
  getObserveManyBelongingsAtom: <FetchedRecordType extends EntityRecord>(
    model: Model<FetchedRecordType>,
    propId: BelongsToRelationPropsOfType<FetchedRecordType, EntityRecordType>,
    options?: {
      constraints?: CombinedQueryConstraint[];
    },
  ) => Atom<EntityQueryResults<FetchedRecordType>>;
  getObserveManyOwnersAtom: <FetchedRecordType extends EntityRecord>(
    model: Model<FetchedRecordType>,
    propId: BelongsToRelationPropsOfType<EntityRecordType, FetchedRecordType>,
    options?: {
      constraints?: CombinedQueryConstraint[];
    },
  ) => Atom<EntityQueryResults<FetchedRecordType>>;
};

export type HydratedEntity<T extends EntityRecord = EntityRecord> =
  Entity<T> & {
    data: T;
  };

export type EntityQueryResults<T extends EntityRecord = EntityRecord> = {
  entities: HydratedEntity<T>[] | undefined;
  loading: boolean;
  error: Error | undefined;
  snapshot: QuerySnapshot<DocumentData> | undefined;
};

export type ModelOptions<T extends EntityRecord = EntityRecord> = {
  collectionPath: string;
  labels: {
    singular: string;
    plural: string;
  };
  getCreatorId?: () => string;
  getDefaultRecord: () => Omit<T, keyof EntityRecord>;
  version: string;
};

export type SerializedQueryConstraint = [QueryConstraint, string];

export type CombinedQueryConstraint =
  | QueryConstraint
  | SerializedQueryConstraint;

export type QueryOptions = {
  constraints?: CombinedQueryConstraint[];
};

export type QueryComposite = {
  hash: string;
  query: Query<DocumentData, DocumentData>;
};

export type Model<T extends EntityRecord = EntityRecord> = {
  getBaseQueryComposite: (
    constraints?: CombinedQueryConstraint[],
  ) => QueryComposite;
  getEntities: (queryOptions?: QueryOptions) => Promise<Entity<T>[]>;
  getObserveEntitiesAtom: (
    queryOptions?: QueryOptions,
  ) => Atom<EntityQueryResults<T>>;
  labels: {
    singular: string;
    plural: string;
  };
  collectionPath: string;
  collectionRef: CollectionReference<DocumentData, DocumentData>;
  createEntityInstance: (
    options: Omit<CreateEntityOptions<T>, "model">,
  ) => Entity<T>;
  create: (
    idOrData?: string | PartialEntityRecord<T>,
    data?: PartialEntityRecord<T>,
  ) => Promise<Entity<T>>;
  createEntityRecord: (data: PartialEntityRecord<T>) => T;
};
